import React, { useEffect, useState } from 'react';
import { 
  Flower2, Building2, Globe, Heart, Users, ChevronRight,
  Briefcase, Award, ExternalLink, ShoppingBag
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import api from '../services/api';

const Partners = () => {
  const [partners, setPartners] = useState({ partners: [], byType: {}, typeLabels: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await api.get('/partners/public');
        setPartners(response.data);
      } catch (error) {
        console.error('Error fetching partners:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'government': return <Building2 className="w-6 h-6" />;
      case 'ngo': return <Heart className="w-6 h-6" />;
      case 'international_org': return <Globe className="w-6 h-6" />;
      case 'pharmaceutical': return <Briefcase className="w-6 h-6" />;
      case 'ministry': return <Users className="w-6 h-6" />;
      default: return <Award className="w-6 h-6" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'government': return 'from-blue-500 to-blue-700';
      case 'ngo': return 'from-green-500 to-green-700';
      case 'international_org': return 'from-purple-500 to-purple-700';
      case 'pharmaceutical': return 'from-cyan-500 to-cyan-700';
      case 'ministry': return 'from-orange-500 to-orange-700';
      default: return 'from-gray-500 to-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900">
      {/* Header */}
      <div className="bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Flower2 className="w-8 h-8 text-pink-300" />
            <span className="text-xl font-bold text-white">Fleur de Vie</span>
          </Link>
          <Link to="/partners/apply">
            <Button className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700">
              Devenir Partenaire
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Nos Partenaires
          </h1>
          <p className="text-pink-200 text-lg max-w-2xl mx-auto">
            Ensemble, nous œuvrons pour améliorer la santé reproductive et l'éducation 
            des femmes à travers le monde.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20 text-center">
            <p className="text-3xl font-bold text-white">{partners.totalCount || 0}</p>
            <p className="text-pink-200 text-sm">Partenaires</p>
          </Card>
          <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20 text-center">
            <p className="text-3xl font-bold text-white">5</p>
            <p className="text-pink-200 text-sm">Types d'organisations</p>
          </Card>
          <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20 text-center">
            <p className="text-3xl font-bold text-white">15+</p>
            <p className="text-pink-200 text-sm">Pays représentés</p>
          </Card>
          <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20 text-center">
            <p className="text-3xl font-bold text-white">1M+</p>
            <p className="text-pink-200 text-sm">Femmes impactées</p>
          </Card>
        </div>

        {/* Partners by Type */}
        {Object.entries(partners.byType || {}).map(([type, typePartners]) => (
          typePartners.length > 0 && (
            <div key={type} className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getTypeColor(type)} flex items-center justify-center text-white`}>
                  {getTypeIcon(type)}
                </div>
                <h2 className="text-2xl font-bold text-white">
                  {partners.typeLabels?.[type] || type}
                </h2>
                <span className="bg-white/20 text-white text-sm px-2 py-1 rounded-full">
                  {typePartners.length}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {typePartners.map((partner) => (
                  <Card 
                    key={partner.id}
                    className="p-6 bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/15 transition-all group"
                  >
                    <div className="flex items-start gap-4">
                      {partner.logo ? (
                        <img 
                          src={partner.logo} 
                          alt={partner.name}
                          className="w-16 h-16 rounded-lg object-cover bg-white"
                        />
                      ) : (
                        <div className={`w-16 h-16 rounded-lg bg-gradient-to-br ${getTypeColor(type)} flex items-center justify-center text-white`}>
                          {getTypeIcon(type)}
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="text-white font-semibold text-lg mb-1">
                          {partner.name}
                        </h3>
                        <p className="text-pink-300 text-sm mb-2">{partner.country}</p>
                        <p className="text-pink-100 text-sm line-clamp-2">
                          {partner.description}
                        </p>
                      </div>
                    </div>
                    
                    {partner.website && (
                      <a 
                        href={partner.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 flex items-center gap-2 text-pink-300 hover:text-white text-sm transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Visiter le site
                      </a>
                    )}
                    
                    {partner.hasMarketplace && partner.marketplaceUrl && (
                      <a 
                        href={partner.marketplaceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 flex items-center gap-2 text-purple-300 hover:text-white text-sm transition-colors bg-purple-500/20 px-3 py-2 rounded-lg"
                      >
                        <ShoppingBag className="w-4 h-4" />
                        Visiter la boutique
                      </a>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          )
        ))}

        {/* Marketplaces Section */}
        {partners.withMarketplace && partners.withMarketplace.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white">
                <ShoppingBag className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-bold text-white">Boutiques Partenaires</h2>
              <span className="bg-purple-500/30 text-purple-200 text-sm px-2 py-1 rounded-full">
                {partners.withMarketplace.length}
              </span>
            </div>
            <p className="text-pink-200 mb-6">
              Découvrez les produits et services de nos partenaires
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {partners.withMarketplace.map((partner) => (
                <Card 
                  key={partner.id}
                  className="p-6 bg-gradient-to-br from-purple-600/20 to-pink-600/20 backdrop-blur-md border-purple-400/30 hover:border-purple-400/50 transition-all"
                >
                  <div className="flex items-start gap-4 mb-4">
                    {partner.logo ? (
                      <img 
                        src={partner.logo} 
                        alt={partner.name}
                        className="w-16 h-16 rounded-lg object-cover bg-white"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-purple-500/30 flex items-center justify-center">
                        <ShoppingBag className="w-8 h-8 text-purple-300" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-white font-semibold text-lg">{partner.name}</h3>
                      <p className="text-purple-200 text-sm">{partner.country}</p>
                    </div>
                  </div>
                  {partner.marketplaceDescription && (
                    <p className="text-pink-100 text-sm mb-4 line-clamp-2">
                      {partner.marketplaceDescription}
                    </p>
                  )}
                  <a 
                    href={partner.marketplaceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full"
                  >
                    <Button className="w-full bg-purple-500 hover:bg-purple-600">
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Accéder à la boutique
                    </Button>
                  </a>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* No Partners */}
        {partners.totalCount === 0 && (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 text-pink-300/50 mx-auto mb-4" />
            <h3 className="text-white text-xl font-semibold mb-2">
              Aucun partenaire pour le moment
            </h3>
            <p className="text-pink-200 mb-6">
              Soyez le premier à rejoindre notre réseau de partenaires
            </p>
            <Link to="/partners/apply">
              <Button className="bg-gradient-to-r from-pink-500 to-purple-600">
                Devenir Partenaire
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}

        {/* CTA Section */}
        <Card className="mt-16 p-8 bg-gradient-to-r from-purple-600 to-pink-600 border-0 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Rejoignez notre réseau de partenaires
          </h2>
          <p className="text-white/80 mb-6 max-w-2xl mx-auto">
            Accédez à des données anonymisées précieuses pour vos recherches, 
            programmes de santé et politiques publiques.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/partners/apply">
              <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
                Soumettre une demande
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                En savoir plus
              </Button>
            </Link>
          </div>
        </Card>
      </div>

      {/* Footer */}
      <div className="bg-purple-900/50 border-t border-white/10 py-8 mt-16">
        <div className="max-w-6xl mx-auto px-4 text-center text-pink-200 text-sm">
          <p>© 2026 Fleur de Vie. Tous droits réservés.</p>
          <p className="mt-2">
            Toutes les données partagées sont anonymisées et agrégées.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Partners;
